// libraries
import React, { useRef, useState, useEffect } from "react";

// components
import clsx from "clsx";

// utils
import { isNonWhitespaceString } from "/lib/reactutils/utils";

// props
export interface Props {
  title?: string;
  content?: string; // HTML
  variant?: "dark" | "light" | "white";
  initiallyCollapsed?: boolean;
  /**
   * Type selector controlling collapsed preview behavior:
   * - 'normal': Not expandable (no toggle button)
   * - 'expandablePreview': Collapsed shows a short faded preview
   * - 'expandableTitle': Collapsed shows only the title
   */
  type?: "normal" | "expandablePreview" | "expandableTitle";
  className?: string;
  /** Content region id used for aria-controls. Required when component is collapsible. */
  contentId?: string;
  /** Heading id referenced by aria-labelledby. Required when `title` is present. */
  headingId?: string;
}

export default function MacroFactbox({
  title,
  content,
  variant = "dark",
  initiallyCollapsed = false,
  type,
  className,
  contentId,
  headingId,
}: Props) {
  // Determine behavior from type + initiallyCollapsed for backwards compatibility
  // Default type:
  // - if "initiallyCollapsed" provided, assume expandablePreview (old behavior)
  // - else default to 'normal'
  const resolvedType: "normal" | "expandablePreview" | "expandableTitle" =
    type ?? (initiallyCollapsed ? "expandablePreview" : "normal");

  const hasTitle = isNonWhitespaceString(title);
  const hasExternalIds = Boolean(contentId && headingId);
  const isCollapsible = resolvedType !== "normal";
  const canCollapse = isCollapsible && hasExternalIds;
  // If user provided initiallyCollapsed explicitly, respect it; otherwise for expandable types start collapsed by default
  const startCollapsed = canCollapse ? initiallyCollapsed ?? true : false;
  const [isOpen, setIsOpen] = useState(!startCollapsed);

  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        // Measure the actual content height for smooth animations
        const height = contentRef.current.scrollHeight;
        setContentHeight(height);
      }
    };

    // Initial measurement
    updateHeight();

    // Update on window resize
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [content]);

  const rootStateClass = isOpen ? "is-open" : "is-collapsed";
  const typeClass =
    resolvedType === "expandableTitle"
      ? "macro-factbox--collapse-title"
      : resolvedType === "expandablePreview"
      ? "macro-factbox--collapse-preview"
      : undefined;

  const regionProps =
    hasTitle && headingId ? { "aria-labelledby": headingId } : undefined;

  return (
    <div
      className={clsx(
        "macro-factbox",
        "rich-text",
        `macro-factbox--${variant}`,
        typeClass,
        className,
        rootStateClass
      )}
      role="region"
      {...regionProps}
    >
      <div className="macro-factbox__body">
        <div className="macro-factbox__text">
          {hasTitle && headingId && (
            <h2 id={headingId} className="macro-factbox__title">
              {title}
            </h2>
          )}

          <div
            className="macro-factbox__content rich-text"
            ref={contentRef}
            id={contentId}
            style={
              {
                "--mf-height": `${contentHeight}px`,
              } as React.CSSProperties
            }
          >
            <div
              dangerouslySetInnerHTML={{ __html: content }}
              suppressHydrationWarning
            />
          </div>
        </div>

        {canCollapse && (
          <button
            type="button"
            className="macro-factbox__toggle"
            aria-expanded={isOpen}
            aria-controls={contentId}
            aria-label={isOpen ? "Lukk faktaboks" : "Åpne faktaboks"}
            onClick={() => setIsOpen((v) => !v)}
          />
        )}
      </div>
    </div>
  );
}
